import dashboardService from 'ApiService/Admin/dashboardService';
import Card from 'components/card';
import BarChart from 'components/charts/BarChart';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import BatchFeesChart from './BatchFeesChart';
import InputField from 'components/fields/InputField';

const SchoolFeesChart = () => {
  const [loading, setLoading] = useState(true);
  const [isBatchChartVisible, setIsBatchChartVisible] = useState(false);
  const [summaryData, setSummaryData] = useState({
    totalExpectedFees: 0,
    totalPaidFees: 0,
    totalRemainingFees: 0,
  });
  const [feesChartData, setFeesChartData] = useState([]);
  const [barChartDataFees, setBarChartDataFees] = useState([
    { name: 'Total Fees', data: [] },
    { name: 'Paid Fees', data: [] },
  ]);
  const [isCardVisible, setIsCardVisible] = useState(true);
  const [xAxisCategories, setXAxisCategories] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterApplied, setFilterApplied] = useState(false);

  const selectedBranch = state?.selectedBranch;

  async function getData() {
    setLoading(true);
    try {
      const response = await dashboardService.getFeesDataForChart(
        selectedBranch,
        startDate,
        endDate
      );

      if (response.data.success) {
        const { feesChartData, summary } = response.data;

        setFeesChartData(feesChartData);
        setSummaryData({
          totalExpectedFees: summary.totalExpectedFees,
          totalPaidFees: summary.totalPaidFees,
          totalRemainingFees: summary.totalRemainingFees,
        });

        const totalFeesData = [];
        const paidFeesData = [];
        const courseNames = [];
        const courseId = [];

        feesChartData.forEach((data) => {
          courseId.push(data.courseId);
          courseNames.push(data.courseName);
          totalFeesData.push(data.totalFees);
          paidFeesData.push(data.paidFees);
        });

        setBarChartDataFees([
          { name: 'Total Fees', data: totalFeesData },
          { name: 'Paid Fees', data: paidFeesData },
        ]);
        setXAxisCategories(courseNames);
      } else {
        toast.error('Failed to fetch data');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getData();
  }, [selectedBranch]);

  const barChartOptionsFees = {
    chart: {
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          handleBarClick(config.dataPointIndex);
        },
      },
    },
    tooltip: { theme: 'dark' },
    xaxis: {
      categories: xAxisCategories,
      labels: {
        show: true,
        style: { colors: '#A3AED0', fontSize: '12px', fontWeight: '500' },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      show: false,
      labels: { show: true, style: { colors: '#CBD5E0', fontSize: '14px' } },
    },
    grid: {
      show: false,
      strokeDashArray: 5,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    fill: {
      type: 'gradient',
      gradient: {
        type: 'vertical',
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        colorStops: [
          [
            { offset: 0, color: 'var(--color-500)', opacity: 1 },
            { offset: 100, color: '#6F32E0', opacity: 0.0 },
          ],
          [
            { offset: 0, color: '#FF6384', opacity: 1 },
            { offset: 100, color: 'rgba(255, 255, 255, 1)', opacity: 0.0 },
          ],
        ],
      },
    },
    dataLabels: { enabled: false },
    plotOptions: { bar: { borderRadius: 5, columnWidth: '20px' } },
    legend: {
      markers: { fillColors: ['var(--color-500)', '#FF6384'] },
      labels: { colors: 'dark:#CBD5E0' },
    },
  };
  const handleFilterClick = () => {
    getData();
    setFilterApplied(true);
  };

  const handleResetClick = () => {
    setStartDate('');
    setEndDate('');
    setFilterApplied(false);
    getData();
  };

  const handleBarClick = (dataPointIndex) => {
    const selectedCourseData = feesChartData.find(
      (data) => data.courseName === xAxisCategories[dataPointIndex]
    );

    if (selectedCourseData) {
      setSelectedCourse({
        courseId: selectedCourseData.courseId,
        courseName: selectedCourseData.courseName,
        startDate,
        endDate,
      });

      setIsBatchChartVisible(true);
      setIsCardVisible(false);
    }
  };

  const handleBatchChartClose = () => {
    setIsBatchChartVisible(false);
    setSelectedCourse(null);
    setIsCardVisible(true);
  };

  return (
    <div>
      {isCardVisible && (
        <Card extra="pb-7 p-4" style={{ border: '3px solid var(--color-500)' }}>
          <div className="mb-4 flex flex-wrap items-end gap-4">
            <div>
              <InputField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded border px-2 py-1"
              />
            </div>
            <div>
              <InputField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded border px-2 py-1"
              />
            </div>
            <button
              onClick={handleFilterClick}
              className="rounded bg-blue-500 px-4 py-2 text-white"
            >
              Filter
            </button>
            <button
              onClick={handleResetClick}
              className="text-black rounded-md bg-gray-600 px-4 py-2 transition hover:bg-gray-800"
            >
              Reset
            </button>
          </div>

          <div className="flex flex-col items-start justify-between lg:flex-row lg:items-center">
            <div className="mb-4 lg:mb-0 lg:mr-4">
              <p className="text-sm font-medium leading-4 text-gray-600">
                Fees Overview
              </p>
              <div className="flex flex-wrap gap-4">
                <p className="text-lg font-bold text-navy-700 dark:text-white">
                  Total Expected:{' '}
                  {summaryData.totalExpectedFees.toLocaleString()}
                </p>
                <p className="text-lg font-bold text-green-600">
                  Paid: {summaryData.totalPaidFees.toLocaleString()}
                </p>
                <p className="text-lg font-bold text-red-600">
                  Remaining: {summaryData.totalRemainingFees.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="h-[300px] w-full pb-0 pt-10">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <BarChart
                chartData={barChartDataFees}
                chartOptions={barChartOptionsFees}
                onDataPointClick={handleBarClick}
              />
            )}
          </div>
        </Card>
      )}

      {isBatchChartVisible && selectedCourse && (
        <BatchFeesChart
          selectedCourse={selectedCourse}
          onClose={handleBatchChartClose}
        />
      )}
    </div>
  );
};

export default SchoolFeesChart;
